'use client';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ScrollAnimation from '@/components/ScrollAnimation';
import { Zap, Lock, Sparkles, ArrowRight, CheckCircle2, Server, Brain, Globe, Clock, AlertTriangle, EyeOff } from 'lucide-react';
import Link from 'next/link';
import { softwareApplicationSchema, breadcrumbSchema } from '@/lib/utils/structured-data';
import { SITE_URL } from '@/lib/site';

const failures = [
  {
    icon: Brain,
    title: 'Context blindness',
    body: 'Standard firewalls look for SQL keywords but miss natural-language attacks. They can\u2019t tell a user asking about code from a user injecting it.',
  },
  {
    icon: EyeOff,
    title: 'They don\u2019t see data leaving',
    body: 'A WAF inspects requests coming in. It doesn\u2019t catch secrets, API keys or PII flowing out of your app - the failure mode that sinks AI-built apps.',
  },
  {
    icon: AlertTriangle,
    title: 'New attack vectors',
    body: 'Prompt injection, jailbreaks and token smuggling bypass regex filters. You need detection built for adversarial AI inputs.',
  },
];

const values = [
  {
    icon: Zap,
    title: 'Deploy where your app already runs',
    body: 'One line in any Node or Next.js app, or a Docker proxy in front of any other stack (Java, Python, Go, PHP). No re-architecture.',
    chips: ['npm i @silker-ai/agent', 'app.use(silker())', 'docker run silker-proxy'],
  },
  {
    icon: Sparkles,
    title: 'AI-aware detection, on your infra',
    body: 'Beyond keyword matching: we inspect request structure and your LLM routes to flag prompt injection and data leaks. Detection runs in your runtime - your traffic never passes through us.',
    chips: ['Prompt injection', 'Data-leak prevention', 'OWASP basics'],
  },
];

const whyUsBreadcrumb = breadcrumbSchema([
  { name: 'Home', url: SITE_URL },
  { name: 'Why Silker AI', url: `${SITE_URL}/why-us` },
]);

export default function WhyUsPage() {
  return (
    <div className="min-h-screen bg-black">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareApplicationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(whyUsBreadcrumb) }} />
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-900/10 via-black to-black"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]"></div>
      </div>

      <Header />
      <ScrollAnimation />

      <main>
        <section className="relative pt-32 pb-24">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-20" data-scroll-animate="fade-up">
              <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-6">
                <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700"></div>
                Why Silker
                <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700"></div>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
                Security built for AI-built apps
              </h1>
              <p className="text-lg text-neutral-400 max-w-2xl mx-auto leading-relaxed">
                WAFs were designed for a different era. Silker runs where your app runs - as an SDK, a Cloudflare Worker
                or a Docker proxy - and catches the prompt injections and data leaks a WAF never sees.
              </p>
            </div>

            {/* Why traditional security fails */}
            <div className="mb-28" data-scroll-animate="fade-up">
              <h2 className="text-3xl font-bold text-white mb-10 text-center">Why a WAF isn&apos;t enough</h2>
              <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                {failures.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <div key={i} className="bg-neutral-950 p-8">
                      <div className="w-12 h-12 rounded-none bg-neutral-800 flex items-center justify-center mb-6">
                        <Icon className="w-6 h-6 text-neutral-300" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3">{f.title}</h3>
                      <p className="text-neutral-400 leading-relaxed text-sm">{f.body}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Core value props */}
            <div className="space-y-6 mb-28">
              {values.map((v, i) => {
                const Icon = v.icon;
                return (
                  <div
                    key={i}
                    className="rounded-none border border-white/10 bg-neutral-900/30 p-8 md:p-12"
                    data-scroll-animate="fade-up"
                  >
                    <div className="flex flex-col md:flex-row items-start gap-8">
                      <div className="w-14 h-14 rounded-none bg-rose-500/5 border border-rose-500/20 flex items-center justify-center shrink-0">
                        <Icon className="w-7 h-7 text-rose-400" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold mb-4 text-white">{v.title}</h2>
                        <p className="text-neutral-400 leading-relaxed mb-6 text-lg">{v.body}</p>
                        <div className="flex flex-wrap gap-3">
                          {v.chips.map((c, j) => (
                            <span key={j} className="px-3 py-1 rounded-none bg-neutral-800 text-sm text-neutral-300 border border-neutral-700 font-mono">
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Privacy & compliance - honest */}
            <div className="grid md:grid-cols-2 gap-12 items-start mb-28" data-scroll-animate="fade-up">
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">Privacy by design</h2>
                <p className="text-neutral-400 leading-relaxed mb-6">
                  Your data stays yours. Silker is built so the sensitive parts never leave your infrastructure.
                </p>
                <ul className="space-y-6">
                  <li className="flex items-start gap-4">
                    <div className="mt-1 p-2 rounded-none bg-blue-500/10 border border-blue-500/20">
                      <Lock className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium mb-1">PII redaction</h3>
                      <p className="text-sm text-neutral-400">Sensitive data is redacted before anything is logged or sent to the dashboard.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="mt-1 p-2 rounded-none bg-emerald-500/10 border border-emerald-500/20">
                      <Globe className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium mb-1">Built for GDPR</h3>
                      <p className="text-sm text-neutral-400">Designed with EU data rules in mind. SOC 2 Type II is on our roadmap, not yet certified - and we&apos;ll say so until it is.</p>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="rounded-none border border-white/10 bg-neutral-900/40 p-8">
                <div className="flex items-center justify-between mb-8">
                  <div className="text-white font-bold">Compliance status</div>
                  <div className="px-3 py-1 rounded-none bg-amber-500/10 text-amber-400 text-xs border border-amber-500/20">Honest &amp; current</div>
                </div>
                <div className="space-y-3">
                  <StatusRow label="Data encryption (in transit)" state="done" />
                  <StatusRow label="PII redaction before logging" state="done" />
                  <StatusRow label="Access control & audit logs" state="done" />
                  <StatusRow label="SOC 2 Type II" state="progress" />
                  <StatusRow label="HIPAA" state="planned" />
                </div>
              </div>
            </div>

            <div className="text-center" data-scroll-animate="fade-up">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">Secure what AI shipped</h2>
              <p className="text-base text-neutral-400 mb-8">Start with a free scan - no credit card.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  href="/#scan"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-black rounded-none text-sm font-semibold hover:bg-neutral-200 transition-colors"
                >
                  Scan your app - free
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  href="/architecture"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/5 text-white rounded-none text-sm font-semibold hover:bg-white/10 transition-colors border border-white/10"
                >
                  See how it works
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function StatusRow({ label, state }: { label: string; state: 'done' | 'progress' | 'planned' }) {
  const map = {
    done: { text: 'Live', cls: 'text-emerald-400', icon: <CheckCircle2 className="w-4 h-4 text-emerald-500" /> },
    progress: { text: 'In progress', cls: 'text-amber-400', icon: <Clock className="w-4 h-4 text-amber-400" /> },
    planned: { text: 'Planned', cls: 'text-neutral-500', icon: <Server className="w-4 h-4 text-neutral-600" /> },
  } as const;
  const s = map[state];
  return (
    <div className="flex items-center justify-between p-4 rounded-none bg-black/40 border border-neutral-800">
      <span className="text-neutral-300 text-sm">{label}</span>
      <span className={`inline-flex items-center gap-2 text-xs ${s.cls}`}>
        {s.icon}
        {s.text}
      </span>
    </div>
  );
}
